import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FuseConfigService } from '@fuse/services/config.service';
import { AuthenticationService } from 'app/core/auth/services/authentication.service';
import {
  getSsoErrorMessageKey,
  parseSsoClientError,
  SSO_EXCHANGE_UNEXPECTED,
  SsoContractErrorView,
} from 'app/core/auth/sso/sso-error.model';
import { AuthLoginStatusEnum } from 'app/models/auth.model';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CurrentUserService } from '../../services/current-user.service';
import { LoginErrorsEnum } from '../login/login.component';
import { FuseInnerScrollDirective } from '../../../../../@fuse/directives/fuse-inner-scroll/fuse-inner-scroll.directive';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { MatError } from '@angular/material/input';

// Only allow same-origin, path-relative redirects: reject protocol-relative ('//host'),
// absolute ('scheme://host'), and backslash variants attackers use to smuggle a host past naive checks.
function isSafeInternalReturnUrl(url: string): boolean {
  return /^\/(?!\/)/.test(url) && !/[:\\]/.test(url);
}

@Component({
  selector: 'app-sso-callback',
  templateUrl: './sso-callback.component.html',
  styleUrls: ['./sso-callback.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FuseInnerScrollDirective, MatProgressSpinner, MatButton, MatError, RouterLink, TranslatePipe],
})
export class SsoCallbackComponent implements OnInit {
  readonly loading = signal(false);
  readonly noHandshakeCode = signal(false);
  readonly errorAuth = signal<LoginErrorsEnum | null>(null);
  readonly contractError = signal<SsoContractErrorView | null>(null);

  readonly LoginErrorsEnum = LoginErrorsEnum;
  readonly ssoMessageKey = getSsoErrorMessageKey;

  private readonly _fuseConfigService = inject(FuseConfigService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translate = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    this._fuseConfigService.config = {
      layout: {
        navbar: { hidden: true },
        toolbar: { hidden: true },
        footer: { hidden: true },
        sidepanel: { hidden: true },
      },
    };
  }

  ngOnInit(): void {
    const qpm = this._route.snapshot.queryParamMap;
    const ssoErrorParam = qpm.get('sso_error');
    if (ssoErrorParam !== null && ssoErrorParam !== '') {
      const code = Number.parseInt(ssoErrorParam, 10);
      if (!Number.isNaN(code)) {
        this.contractError.set({
          code,
          ref: qpm.get('sso_ref') ?? '',
        });
        return;
      }
    }

    const handshakeCode = qpm.get('code');
    if (!handshakeCode) {
      this.noHandshakeCode.set(true);
      return;
    }

    this.loading.set(true);
    this._authenticationService
      .completeSamlLogin(handshakeCode)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          if (!response || response?.status === AuthLoginStatusEnum.auth2faRequired) {
            this.loading.set(false);
            this.errorAuth.set(LoginErrorsEnum.required2FA);
            return;
          }

          this._currentUserService
            .loadUserFromRemote()
            .pipe(
              map((user) => {
                if (user?.enabled === false) {
                  this.errorAuth.set(LoginErrorsEnum.userDisabled);
                  this._authenticationService.logout();
                  throw new Error('User disabled');
                }
                return user;
              }),
              catchError(() => {
                this.loading.set(false);
                if (!this.errorAuth()) {
                  this.errorAuth.set(LoginErrorsEnum.serverError);
                  this._authenticationService.logout();
                }
                return EMPTY;
              }),
              takeUntilDestroyed(this._destroyRef),
            )
            .subscribe({
              next: () => {
                const returnUrl = this._route.snapshot.queryParamMap.get('returnUrl');
                if (returnUrl && isSafeInternalReturnUrl(returnUrl)) {
                  this._router.navigateByUrl(returnUrl, { replaceUrl: true });
                } else {
                  this._router.navigate(['dashboard'], { replaceUrl: true });
                }
              },
            });
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          const parsed = parseSsoClientError(err.error);
          if (parsed) {
            this.contractError.set({ code: parsed.sso_error, ref: parsed.sso_ref });
          } else {
            this.contractError.set({ code: SSO_EXCHANGE_UNEXPECTED, ref: '' });
          }
        },
      });
  }

  copySsoRef(): void {
    const ref = this.contractError()?.ref;
    if (!ref) {
      return;
    }
    void navigator.clipboard.writeText(ref).then(() => {
      this._toastrService.success(this._translate.instant('sso.ref.copied'));
    });
  }
}
